import { useEffect, useRef, useState } from "react";
import { ActivityEvent, api, SnapshotView, TimelineGap } from "./api";
import { bytes, localInput, reasonLabel } from "./format";
import { FilterBar, PageHeader } from "./ui";

export function TimelinePage({ deviceId }: { deviceId: string }) {
  const requestedAt = new URLSearchParams(location.hash.split("?", 2)[1] ?? "").get("at");
  const center = requestedAt ? new Date(requestedAt) : new Date();
  const [from, setFrom] = useState(localInput(new Date(center.getTime() - (requestedAt ? 900000 : 86400000))));
  const [to, setTo] = useState(localInput(new Date(center.getTime() + (requestedAt ? 900000 : 0))));
  const [items, setItems] = useState<SnapshotView[]>([]);
  const [gaps, setGaps] = useState<TimelineGap[]>([]);
  const [cursor, setCursor] = useState<string>();
  const [selected, setSelected] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [speed, setSpeed] = useState(1);
  const [context, setContext] = useState<ActivityEvent | null | undefined | "error">(undefined);
  const [retry, setRetry] = useState(0);
  const [openWindows, setOpenWindows] = useState<{ process: string; title: string }[] | null>(null);
  const [activity, setActivity] = useState<ActivityEvent[]>([]);
  const [activityFilter, setActivityFilter] = useState("");
  const windowsCache = useRef(new Map<string, { process: string; title: string }[] | null>());
  const contextCache = useRef(new Map<string, ActivityEvent | null>());
  const timelineTrackRef = useRef<HTMLDivElement>(null);
  const scrollTrack = (direction: number) => {
    const track = timelineTrackRef.current;
    if (!track) return;
    // Passo derivado do item real (200px + gap), em vez de um valor fixo que
    // diverge se o CSS mudar.
    const first = track.firstElementChild as HTMLElement | null;
    const gap = Number.parseFloat(getComputedStyle(track).columnGap) || 10;
    const step = (first?.offsetWidth ?? 200) + gap;
    track.scrollBy({ left: direction * step, behavior: "smooth" });
  };
  useEffect(() => {
    const track = timelineTrackRef.current;
    track?.querySelectorAll("button")[selected]?.scrollIntoView({ block: "nearest", inline: "center" });
  }, [selected]);
  const retryContext = () => {
    const snapshot = items[selected];
    if (snapshot) {
      contextCache.current.delete(snapshot.id);
      windowsCache.current.delete(snapshot.id);
    }
    setContext(undefined);
    setOpenWindows(null);
    setRetry((value) => value + 1);
  };
  const openActivity = (event: ActivityEvent) => {
    const index = event.nearestSnapshotId
      ? items.findIndex((item) => item.id === event.nearestSnapshotId)
      : -1;
    if (index >= 0) {
      setSelected(index);
      setPlaying(false);
    }
  };
  // Os termos são procurados na string combinada (processo + título da janela), e todos
  // precisam aparecer nela: "chrome google" casa processo chrome + janela com "google",
  // mesmo que as palavras venham de campos diferentes.
  const filterTerms = activityFilter.trim().toLowerCase().split(/\s+/).filter(Boolean);
  const filteredActivity = filterTerms.length
    ? activity.filter((event) => {
        const haystack = `${event.processName ?? ""} ${event.windowTitle ?? ""}`.toLowerCase();
        return filterTerms.every((term) => haystack.includes(term));
      })
    : activity;

  useEffect(() => {
    const snapshot = items[selected];
    if (!snapshot) {
      setContext(undefined);
      setOpenWindows(null);
      return;
    }
    if (contextCache.current.has(snapshot.id)) {
      setContext(contextCache.current.get(snapshot.id) ?? null);
      setOpenWindows(windowsCache.current.get(snapshot.id) ?? null);
      return;
    }
    let cancelled = false;
    setContext(undefined);
    setOpenWindows(null);
    const at = new Date(snapshot.capturedAt).getTime();
    const timer = window.setTimeout(() => {
      api
        .searchActivity({
          deviceId,
          from: new Date(at - 120_000).toISOString(),
          to: new Date(at + 30_000).toISOString(),
        })
        .then((page) => {
          const nearest =
            page.items
              .filter((item) => item.processName || item.windowTitle)
              .sort(
                (a, b) =>
                  Math.abs(new Date(a.agentTimestamp).getTime() - at) -
                  Math.abs(new Date(b.agentTimestamp).getTime() - at),
              )[0] ?? null;
          const listEvent = page.items
            .filter((item) => item.eventType === "window_list" && item.attributes?.windows)
            .sort(
              (a, b) =>
                Math.abs(new Date(a.agentTimestamp).getTime() - at) -
                Math.abs(new Date(b.agentTimestamp).getTime() - at),
            )[0];
          let windows: { process: string; title: string }[] | null = null;
          if (listEvent?.attributes?.windows) {
            try {
              const parsed: unknown = JSON.parse(listEvent.attributes.windows);
              if (Array.isArray(parsed)) {
                windows = parsed
                  .filter(
                    (entry): entry is { process?: unknown; title?: unknown } =>
                      entry !== null && typeof entry === "object",
                  )
                  .map((entry) => ({
                    process: String(entry.process ?? ""),
                    title: String(entry.title ?? ""),
                  }))
                  .filter((entry) => entry.process.length > 0);
              }
            } catch {
              windows = null;
            }
          }
          contextCache.current.set(snapshot.id, nearest);
          windowsCache.current.set(snapshot.id, windows);
          if (!cancelled) {
            setContext(nearest);
            setOpenWindows(windows);
          }
        })
        .catch(() => {
          if (!cancelled) {
            setContext("error");
            setOpenWindows(null);
          }
        });
    }, 250);
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [items, selected, deviceId, retry]);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const load = async (next?: string) => {
    setBusy(true);
    setError("");
    try {
      const page = await api.timeline(
        deviceId,
        new Date(from).toISOString(),
        new Date(to).toISOString(),
        next,
      );
      const views = await Promise.all(
        page.items.map((item) => api.snapshotView(deviceId, item.id)),
      );
      setItems((previous) => (next ? [...previous, ...views] : views));
      setGaps(page.offlinePeriods);
      setCursor(page.nextCursor);
      if (!next) {
        setSelected(0);
        // Lista lateral: eventos de atividade do mesmo período das capturas.
        // Uma consulta por carregamento (não por seleção) para não pesar no rate limit.
        const times = views
          .map((view) => new Date(view.capturedAt).getTime())
          .filter((value) => Number.isFinite(value));
        if (times.length > 0) {
          const newest = Math.max(...times);
          const oldest = Math.max(Math.min(...times), newest - 31 * 86400000);
          api
            .searchActivity({
              deviceId,
              from: new Date(oldest).toISOString(),
              to: new Date(newest + 1000).toISOString(),
              limit: 200,
            })
            .then((result) =>
              setActivity(result.items.filter((item) => item.eventType !== "window_list")),
            )
            .catch(() => setActivity([]));
        } else {
          // Sem capturas no período: limpar a lista evita mostrar os eventos do
          // período anterior, o que pareceria atividade "no período vazio".
          setActivity([]);
        }
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : "Falha ao carregar timeline.");
    } finally {
      setBusy(false);
    }
  };
  useEffect(() => {
    void load();
  }, [deviceId]);
  useEffect(() => {
    if (!playing || items.length < 2) return;
    const timer = window.setInterval(
      () => setSelected((index) => (index + 1 < items.length ? index + 1 : 0)),
      2000 / speed,
    );
    return () => clearInterval(timer);
  }, [playing, speed, items.length]);
  const current = items[selected];
  return (
    <>
      <a className="back" href={`#/devices/${deviceId}`}>
        ← Equipamento
      </a>
      <PageHeader
        eyebrow="DVR DE EVIDÊNCIAS"
        title="Timeline"
        subtitle="Navegue pelas capturas sem carregar todo o histórico."
      />
      <FilterBar
        onSubmit={() => {
          // O filtro é do período anterior; limpar evita confundir "sem atividade"
          // com "esqueci o filtro ligado".
          setActivityFilter("");
          void load();
        }}
        actions={
          <button type="submit" disabled={busy}>
            Aplicar período
          </button>
        }
      >
        <label>
          Início
          <input
            type="datetime-local"
            value={from}
            onChange={(e) => setFrom(e.target.value)}
          />
        </label>
        <label>
          Fim
          <input
            type="datetime-local"
            value={to}
            onChange={(e) => setTo(e.target.value)}
          />
        </label>
      </FilterBar>
      {error && <p className="error">{error}</p>}
      {gaps.length > 0 && (
        <section className="offline-strip">
          <strong>Períodos offline detectados</strong>
          {gaps.map((g) => (
            <span key={g.startedAt}>
              {new Date(g.startedAt).toLocaleString("pt-BR")} —{" "}
              {new Date(g.endedAt).toLocaleString("pt-BR")}
            </span>
          ))}
        </section>
      )}
      {current && (
        <section className="dvr">
          <div className="viewer">
            <img
              src={current.downloadUrl}
              alt={`Captura de ${new Date(current.capturedAt).toLocaleString("pt-BR")}`}
            />
            <div className="viewer-meta">
              <strong>
                {new Date(current.capturedAt).toLocaleString("pt-BR")}
              </strong>
              <span>
                {reasonLabel(current.captureReason)} · {current.width}×
                {current.height} ·{" "}
                {bytes(current.contentLength)}
              </span>
              <div className="viewer-actions">
                <a
                  className="small-button"
                  href={current.downloadUrl}
                  target="_blank"
                  rel="noreferrer"
                >
                  Baixar captura
                </a>
                <div className="viewer-context">
                  <span>Em uso no momento</span>
                  {context === undefined ? (
                    <small>Carregando…</small>
                  ) : context === "error" ? (
                    <small>
                      Não foi possível carregar a atividade.{" "}
                      <button className="link-button" onClick={retryContext}>
                        Tentar de novo
                      </button>
                    </small>
                  ) : context === null ? (
                    <small>Sem atividade registrada neste horário.</small>
                  ) : (
                    <strong>
                      {context.processName ?? "—"}
                      {context.windowTitle ? ` · ${context.windowTitle}` : ""}
                    </strong>
                  )}
                  {openWindows && openWindows.length > 0 && (
                    <details className="open-windows">
                      <summary>Janelas abertas ({openWindows.length})</summary>
                      <ul>
                        {openWindows.map((window, index) => (
                          <li key={`${window.process}-${index}`}>
                            <strong>{window.process}</strong>
                            {window.title ? ` · ${window.title}` : ""}
                          </li>
                        ))}
                      </ul>
                    </details>
                  )}
                </div>
              </div>
            </div>
          </div>
          <aside className="activity-rail">
            <div className="rail-head">
              <strong>Atividade</strong>
              <span>{filteredActivity.length}</span>
            </div>
            <input
              className="rail-filter"
              value={activityFilter}
              onChange={(e) => setActivityFilter(e.target.value)}
              placeholder="Filtrar processo ou janela"
              aria-label="Filtrar eventos de atividade"
            />
            <div className="rail-list">
              {filteredActivity.map((event) => {
                const index = event.nearestSnapshotId
                  ? items.findIndex((item) => item.id === event.nearestSnapshotId)
                  : -1;
                return (
                  <button
                    key={event.id}
                    className={index >= 0 && index === selected ? "selected" : ""}
                    onClick={() => openActivity(event)}
                    title={event.windowTitle ?? undefined}
                  >
                    <time>{new Date(event.agentTimestamp).toLocaleTimeString("pt-BR")}</time>
                    <strong>{event.processName ?? event.eventType.replaceAll("_", " ")}</strong>
                    <small>{event.windowTitle ?? ""}</small>
                  </button>
                );
              })}
              {!filteredActivity.length && (
                <p className="muted rail-empty">
                  {activity.length
                    ? "Nenhum evento corresponde ao filtro."
                    : "Sem atividade no período."}
                </p>
              )}
            </div>
          </aside>
          <div className="playback">
            <button
              className="secondary"
              onClick={() => setSelected(Math.max(0, selected - 1))}
            >
              ← Anterior
            </button>
            <button onClick={() => setPlaying((value) => !value)}>
              {playing ? "Pausar" : "Reproduzir"}
            </button>
            <button
              className="secondary"
              onClick={() =>
                setSelected(Math.min(items.length - 1, selected + 1))
              }
            >
              Próxima →
            </button>
            <label>
              Velocidade
              <select
                value={speed}
                onChange={(e) => setSpeed(Number(e.target.value))}
              >
                <option value="0.5">0,5×</option>
                <option value="1">1×</option>
                <option value="2">2×</option>
                <option value="4">4×</option>
              </select>
            </label>
          </div>
        </section>
      )}
      <section className="panel timeline-panel">
        <div className="panel-title">
          <h2>Capturas</h2>
          <span>{items.length} capturas carregadas</span>
        </div>
        {items.length ? (
          <div className="carousel">
            <button className="carousel-nav" onClick={() => scrollTrack(-1)} aria-label="Capturas anteriores">‹</button>
            <div className="carousel-track" ref={timelineTrackRef}>
              {items.map((item, index) => (
                <button
                  key={item.id}
                  className={selected === index ? "selected" : ""}
                  onClick={() => {
                    setSelected(index);
                    setPlaying(false);
                  }}
                >
                  <img loading="lazy" src={item.downloadUrl} alt="" />
                  <span>{new Date(item.capturedAt).toLocaleTimeString("pt-BR")}</span>
                </button>
              ))}
            </div>
            <button className="carousel-nav" onClick={() => scrollTrack(1)} aria-label="Próximas capturas">›</button>
          </div>
        ) : (
          <div className="empty">
            <b>Nenhuma captura no período</b>
            <span>Ajuste as datas ou aguarde o próximo envio.</span>
          </div>
        )}
        {cursor && (
          <div className="load-more">
            <button disabled={busy} onClick={() => load(cursor)}>
              Carregar capturas anteriores
            </button>
          </div>
        )}
      </section>
    </>
  );
}

